import { ROLES } from '@ibe/constants';
import { Button, cn } from '@ibe/ui';
import { Bell, Building2, LogOut, Menu, User } from 'lucide-react';
import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { useAuth } from '../auth/auth-context.js';
import { type AuthenticatedUser } from '../auth/session.js';
import { NAVIGATION, type NavSection } from './navigation.js';

/**
 * The portal shell: a sidebar, a header, and the routed page.
 *
 * Three structural details are accessibility requirements rather than styling.
 *
 * The page is a set of **landmarks** — `banner`, `navigation`, `main`. A screen
 * reader user moves between them directly; without them the only way to reach
 * the content is to tab past every link in the sidebar, on every page.
 *
 * There is a **skip link** first in the tab order for the same reason, for
 * keyboard users who are not using a screen reader.
 *
 * The current page is marked with `aria-current`, not only with a colour.
 */
export function AppLayout(): React.JSX.Element {
  const auth = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // A platform Super Administrator sits outside every tenant and operates the
  // platform itself — tenants and reference data — rather than any tenant's
  // properties, reservations or guests. They see only the platform-only
  // sections; a tenant user never sees them. Item-level permission filtering
  // still applies within the selected sections.
  //
  // A tenant can also have the Super Administrator role, but it is scoped to
  // that tenant and carries modules. Only a platform operator has no modules.
  const user = auth.user;
  const isPlatformOperator =
    user !== null && user.roles.includes(ROLES.SUPER_ADMINISTRATOR) && user.modules.length === 0;

  const visible = NAVIGATION.filter((section) =>
    isPlatformOperator ? section.platformOnly === true : section.platformOnly !== true,
  )
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => auth.canAny(item.permissions)),
    }))
    .filter((section) => section.items.length > 0);

  return (
    /*
     * The shell fills the viewport exactly and does not scroll; the content
     * region scrolls inside it.
     *
     * `h-full`, not `h-screen`, and it depends on `html, body, #root` all
     * having a height — set in `src/styles.css`. `h-screen` is 100vh, which on
     * a mobile browser is measured against the viewport *without* its
     * retracting toolbar, so the shell ends up taller than the space actually
     * available and the page scrolls by the height of that toolbar.
     *
     * With a page-height document instead, scrolling a long form takes the
     * header and the navigation off the top of the screen with it — so the
     * further someone gets through a form, the further they are from Sign out
     * and from every other section. Fixing the frame keeps both in place no
     * matter how long the page is, and is the reason this is done here rather
     * than per screen.
     */
    <div className="flex h-full flex-col overflow-hidden">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-background focus:px-3 focus:py-2"
      >
        Skip to content
      </a>

      <header className="flex h-14 shrink-0 items-center justify-between border-b bg-surface px-4 shadow-card">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            aria-expanded={sidebarOpen}
            aria-controls="sidebar"
            aria-label={sidebarOpen ? 'Hide navigation' : 'Show navigation'}
            onClick={() => {
              setSidebarOpen((open) => !open);
            }}
          >
            <Menu className="h-5 w-5" aria-hidden="true" />
          </Button>
          <span className="flex items-center gap-2 font-semibold">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <Building2 className="h-4 w-4" aria-hidden="true" />
            </span>
            IBE Admin
          </span>
        </div>
      </header>

      {/*
       * `min-h-0` is load-bearing. A flex child defaults to `min-height: auto`,
       * which refuses to shrink below its content — so without it this row
       * grows to the height of the page, `overflow-y-auto` below never has
       * anything to scroll, and the whole document scrolls again instead.
       */}
      <div className="flex min-h-0 flex-1">
        <Sidebar
          sections={visible}
          open={sidebarOpen}
          currentPath={location.pathname}
          user={auth.user}
          onSignOut={() => {
            void auth.logout();
          }}
        />

        {/*
         * The one scroll container. Everything routed into it scrolls here.
         *
         * `relative` is not decoration and removing it reintroduces a page
         * scrollbar. `overflow` only clips a descendant whose containing
         * block is inside the clipper, and an absolutely positioned element
         * takes its containing block from the nearest *positioned* ancestor.
         * A static scroll container therefore does not clip them at all:
         * Radix renders a hidden `position: absolute` native input behind
         * every checkbox, those resolved against the document instead, and
         * they sat at the form's unscrolled coordinates — stretching the page
         * by however far down the form they appeared. Measured at 179px on
         * the tenant form, which is exactly what the header scrolled away by.
         */}
        <main id="main" className="relative min-w-0 flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function Sidebar({
  sections,
  open,
  currentPath,
  user,
  onSignOut,
}: {
  readonly sections: readonly NavSection[];
  readonly open: boolean;
  readonly currentPath: string;
  readonly user: AuthenticatedUser | null;
  readonly onSignOut: () => void;
}): React.JSX.Element {
  return (
    <nav
      id="sidebar"
      aria-label="Main"
      className={cn(
        // `relative` for the same reason as `main`: a scroll container must
        // be a containing block, or an absolutely positioned descendant
        // escapes its clipping and lands on the document.
        'relative flex w-64 shrink-0 flex-col border-r bg-surface md:flex',
        open ? 'flex' : 'hidden',
      )}
    >
      <div className="flex-1 overflow-y-auto p-4">
        {sections.map((section) => (
          <div key={section.label} className="mb-5">
            <h2 className="mb-2 text-caption font-semibold uppercase tracking-wide text-text-subtle">
              {section.label}
            </h2>
            <ul className="space-y-1">
              {section.items.map((item) => {
                const Icon = item.icon;
                // A prefix match, so a detail page keeps its section highlighted.
                // Exact matching leaves the sidebar showing nothing selected the
                // moment someone opens a record, which reads as having navigated
                // out of the section.
                const current = currentPath === item.to || currentPath.startsWith(`${item.to}/`);

                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      aria-current={current ? 'page' : undefined}
                      className={cn(
                        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-text transition-colors hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        current && 'bg-primary-subtle text-primary',
                      )}
                    >
                      {Icon !== undefined ? (
                        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                      ) : null}
                      {item.label}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      <div className="shrink-0 space-y-1 border-t p-4">
        <button
          type="button"
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-text transition-colors hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Bell className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="flex-1 text-left">Notifications</span>
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-caption font-semibold text-destructive-foreground">
            3
          </span>
        </button>

        {user !== null ? (
          <div className="mt-4 rounded-lg border border-border bg-surface-raised p-3">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-muted text-text">
                <User className="h-4 w-4" aria-hidden="true" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-text">{user.displayName}</p>
                <p className="truncate text-caption text-text-muted">{user.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="mt-2 w-full justify-start text-text hover:text-destructive"
              onClick={onSignOut}
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              Sign out
            </Button>
          </div>
        ) : null}
      </div>
    </nav>
  );
}
